import { ChevronDown, Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useLanguage, Language } from "@/contexts/LanguageContext";
import { useNavigate, useLocation } from "react-router-dom";

const languages: { code: Language; name: string; shortName: string }[] = [
  { code: "en", name: "English", shortName: "EN" },
  { code: "th", name: "ไทย", shortName: "TH" },
];

interface LanguageSelectorProps {
  hoverColor?: string;
}

const LanguageSelector = ({ hoverColor = "#2ec27c" }: LanguageSelectorProps) => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();

  const currentLanguage = languages.find((l) => l.code === language) || languages[1]; // Default to Thai

  const handleLanguageChange = (targetLang: Language) => {
    if (targetLang === language) return;
    
    const currentPath = location.pathname;
    const currentSearch = location.search;
    const currentHash = location.hash;
    
    let newPath: string;
    
    if (targetLang === "en") {
      // Switch to English - add /en prefix
      if (currentPath === "/") {
        newPath = "/en";
      } else {
        newPath = `/en${currentPath}`;
      }
    } else {
      // Switch to Thai - remove /en prefix
      if (currentPath === "/en") {
        newPath = "/";
      } else {
        newPath = currentPath.replace(/^\/en/, "") || "/";
      }
    }
    
    navigate(newPath + currentSearch + currentHash);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          size="sm" 
          className="gap-2 hover:bg-transparent"
          style={{ '--hover-color': hoverColor } as React.CSSProperties}
          onMouseEnter={(e) => e.currentTarget.style.color = hoverColor}
          onMouseLeave={(e) => e.currentTarget.style.color = ''}
        >
          <Globe className="w-4 h-4" />
          <span className="hidden sm:inline">{currentLanguage.name}</span>
          <span className="sm:hidden">{currentLanguage.shortName}</span>
          <ChevronDown className="w-3 h-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-background border border-border z-50">
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code)}
            className={`cursor-pointer ${language === lang.code ? "bg-primary/10" : ""} ${lang.code === "th" ? "font-thai" : ""}`}
          >
            {lang.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSelector;
